<template>
  <v-container fluid v-if="show">
    <v-dialog v-model="show" max-width="800px">
      <v-card>
        <v-card-title class="secondary white--text d-flex align-center justify-space-between">
          <span class="header-font" style="font-size: 22px;">
            {{ title }}
          </span>
        </v-card-title>
        <v-card-subtitle class="secondary py-2 white--text d-flex align-center justify-space-between">
          <span class="subheader-font body-2">{{ subtitle }}</span>
        </v-card-subtitle>
        <v-card-text>
          <v-form name="formName" ref="formRef"
            @submit.prevent="onCreateClick()"
            class="d-flex align-center flex-wrap" v-if="activeTemplate">
              <div class="w-100 body-1 font-weight-bold primary--text py-1">
                {{ $t('collections.visibility') }}
              </div>
              <v-radio-group
                v-model="activeTemplate.visibilityId"
                dense
                row
                hide-details
                class="mt-0"
              >
                <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                  <template v-slot:activator="{ on }">
                    <v-radio
                      :value="1"
                      color="primary"
                      v-on="on"
                    >
                      <template v-slot:label>
                        <span v-on="on">{{ $t("collections.privacity.publicTitle")}}</span>
                        <v-icon color="grey" class="mx-1">public</v-icon>
                      </template>
                    </v-radio>
                  </template>
                  <span class="black--text body-1">{{ $t('collections.privacity.publicDescription') }}</span>
                </v-tooltip>
                <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                  <template v-slot:activator="{ on }">
                    <v-radio
                      :value="2"
                      color="primary"
                      v-on="on"
                    >
                      <template v-slot:label>
                        <span v-on="on">{{ $t('collections.privacity.privateTitle')}}</span>
                        <v-icon color="grey" class="mx-1">lock</v-icon>
                      </template>
                    </v-radio>
                  </template>
                  <span class="black--text body-1">{{ $t('collections.privacity.privateDescription') }}</span>
                </v-tooltip>
                <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                  <template v-slot:activator="{ on }">
                    <v-radio
                      :value="3"
                      color="primary"
                      v-on="on"
                    >
                      <template v-slot:label>
                        <span v-on="on">{{ $t("collections.privacity.supervisedTitle")}}</span>
                        <v-icon color="grey" class="mx-1">visibility</v-icon>
                      </template>
                    </v-radio>
                  </template>
                  <span class="black--text body-1">{{ $t('collections.privacity.supervisedDescription') }}</span>
                </v-tooltip>
              </v-radio-group>
            <div class="d-flex align-center w-50 mt-5">
              <v-text-field 
                v-model="activeTemplate.radarName"
                class="slim-input"
                outlined
                dense
                :label="$t('settings.templateName')" 
                clearable
                :rules="radarTemplateRequiredRules"
              ></v-text-field>
            </div>
            <div class="d-flex align-center w-50 mb-4 pl-3">
              <v-checkbox 
                v-model="activeTemplate.defaultRadar"
                color="secondary" 
                hide-details 
                dense
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('settings.radarDefault') }}</span>
                </template>
              </v-checkbox>
            </div>
            <div class="d-flex align-center w-50 mt-0">
              <v-autocomplete v-if="activeTemplate.radarType === 'player' || (activeTemplate.id && activeTemplate.positionId)"
                :items="positions"
                v-model="activeTemplate.positionId"
                class="slim-input"
                outlined 
                dense 
                hide-details
                item-text="name"
                item-value="id"
                :label="this.$t('general.position')" 
              />
            </div>
            <div class="w-100">
              <div class="d-flex align-center mt-2">
                <div class="primary--text font-weight-bold text-h6">{{$t("general.add")}} {{$t("general.stats")}}</div>
              </div>
              <v-col cols="12" class="px-0">
                <GeneralFilter
                  class="elevation-2"
                  :items="availableStatsByProduct"
                  :minLength="1"
                  exactCoincidence
                  :excludedFields="['arrigo', 'category','categoryId','code','id','adar','player','playerScout','statTypeId','team','tooltip']"
                  @filterChange="onFilterChange($event)"
                  @inputChange="onFilterInputChange($event)"
                />
              </v-col>
              <v-col v-if="categories" cols="12" class="pa-0">
                <!-- Product Section -->
                <div class="darkgrey--text font-weight-bold text-body-1">{{ $t("settings.availableProducts") }}</div>
                <v-card rounded elevation="0" outlined class="py-1 px-2 my-1 d-flex">
                  <div class="d-flex flex-wrap align-self-start">
                    <v-tooltip max-width="300"
                      v-for="(product, index) in products"
                      :key="index"
                      top color="lightSecondary lighten-4">
                      <template v-slot:activator="{ on }">
                        <v-chip
                          v-on="on"
                          :outlined="!product.active"
                          class="mr-2 my-1 d-flex justify-center"
                          color="secondary"
                          :label="true"
                          small
                          @click="onProductClick(product)"
                          :disabled="disableCategories || product.disabled"
                        >
                          <v-icon :style="product.icon.startsWith('fa-') ? 'font-size: 10px' : 'font-size: 13px'" 
                            :color="product.active ? 'white' : 'secondary'" class="me-1">
                            {{ product.icon }}
                          </v-icon>
                          <span class="body-2 font-weight-medium">{{ product.name }}</span>
                        </v-chip>
                      </template>
                      <div class="d-flex flex-column black--text body-1">
                        <span class="font-weight-bold">{{ product.name }}</span>
                        <span class="monospace-font body-2">{{ product.desc }}</span>
                      </div>
                    </v-tooltip>
                  </div>
                </v-card>
                <!-- Categories Section -->
                <div class="w-100 body-1 font-weight-bold primary--text pb-1">
                  {{ $t('general.categories') }}
                </div>
                <v-card rounded elevation="0" outlined class="py-1 px-2 my-1 d-flex">
                  <div class="d-flex flex-wrap align-self-start">
                    <v-tooltip max-width="300"
                      v-for="(category, index) in availableCategoriesByProduct"
                      :key="index"
                      top color="lightSecondary lighten-4">
                      <template v-slot:activator="{ on }">
                        <v-chip
                          v-on="on"
                          :outlined="!category.active"
                          class="mr-2 my-1 d-flex justify-center"
                          color="secondary"
                          :label="true"
                          small
                          @click="onCategoryClick(category)"
                          :disabled="disableCategories"
                        >
                          <v-icon v-if="category.product.icon" :style="category.product.icon.startsWith('fa-') ? 'font-size: 10px' : 'font-size: 13px'" 
                            :color="category.active ? 'white' : 'secondary'" class="me-1">
                            {{ category.product.icon }}
                          </v-icon>
                          <span class="body-2 font-weight-medium">{{ category.name }}</span>
                        </v-chip>
                      </template>
                      <div class="d-flex flex-column black--text body-1">
                        <span class="font-weight-bold">{{ category.name }}</span>
                        <span class="monospace-font body-2">{{ category.product.name }}</span>
                      </div>
                    </v-tooltip>
                  </div>
                </v-card>
                <!-- Stats Section -->
                <div v-if="categoryModel" class="w-100 body-1 font-weight-bold primary--text pb-1">
                  {{ categoryModel.name }}
                </div>
                <v-card rounded elevation="0" outlined class="py-1 px-2 my-1 d-flex">
                  <div v-if="filteredStats.length" class="d-flex flex-wrap align-self-start">
                    <v-tooltip max-width="300"
                      v-for="(stat,index) in filteredStats"
                      :key="index"
                      top color="lightSecondary lighten-4">
                      <template v-slot:activator="{ on }">
                        <v-chip
                          v-on="on"
                          :outlined="!stat.active"
                          class="mr-2 my-1 d-flex justify-center"
                          color="lightSecondary"
                          :label="true"
                          small
                          @click="onStatClick(stat)"
                        >
                          <span class="body-2 font-weight-medium" :class="stat.active ? 'white--text' : 'lightSecondary--text'">{{ stat.name }}</span>
                        </v-chip>
                      </template>
                      <!--<span class="black--text body-1">{{ stat.tooltip }}</span>-->
                      <div class="d-flex flex-column black--text body-1">
                        <span class="font-weight-bold">{{ stat.name }}</span>
                        <span class="monospace-font body-2">{{ stat.product.name }}</span>
                        <span v-html="stat.tooltip" v-if="stat.name !== stat.tooltip"></span>
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else class="d-flex justify-center align-center mt-1" style="width: 100%; height: 100%;">
                    <v-alert dense type="warning" color="primary lighten-3" class="py-1 mb-1 body-1 font-weight-medium">{{$t("general.noDataGivenFilters")}}</v-alert>
                  </div>
                </v-card>
                <!-- New Template Section -->
                <div class="d-flex">
                  <div v-if="activeTemplate.stats.length" class="darkgrey--text font-weight-bold text-body-1">
                  {{ $t('general.new') }} '{{ activeTemplate.radarName }}' {{ $t('playerRecruitment.template') }}
                  </div>
                  <div v-if="warningTextTemplates" class="font-weight-bold text-body-1 pl-3 red--text">{{ warningTextTemplates }}</div>
                </div>
                <v-card v-if="activeTemplate.stats.length" rounded elevation="0" outlined class="py-1 px-2 my-1 d-flex">
                  <div class="d-flex px-2 py-1 flex-wrap align-self-start">
                    <div class="stat" v-for="(stat,index) in activeTemplate.stats" :key="index">
                      <drop
                        drop-effect="move"
                        effect-allowed="move"
                        class="flex-grow-1"
                        @drop="onDropStat($event,index)">
                        <drag class="main-stat d-flex"
                          @dragstart="dragging = true;"
                          :transfer-data="{stat:stat,index:index}"
                          drop-effect="move"
                          :effect-allowed="['move']"
                          style="cursor:move;">
                          <template slot="image">
                            <v-chip
                              class="d-flex justify-center"
                              color="green"
                              :label="true"
                              small
                            >
                              <v-icon v-if="stat.product.icon" 
                                :style="stat.product.icon.startsWith('fa-') ? 'font-size: 10px' : 'font-size: 13px'" 
                                color="white" 
                                class="me-1">
                                {{ stat.product.icon }}
                              </v-icon>
                              <span class="body-2 white--text font-weight-medium">{{ stat.name }}</span>
                            </v-chip>
                          </template>
                          <v-badge 
                            :key="index" 
                            :content="index+1" 
                            color="secondary" 
                            class="mr-4 mb-4">
                            <v-tooltip max-width="300"
                              :key="index"
                              top color="lightSecondary lighten-4">
                              <template v-slot:activator="{ on }">
                                <v-chip
                                  v-on="on"
                                  class="d-flex justify-center"
                                  color="green"
                                  :label="true"
                                  small
                                  @click="onStatClick(stat)"
                                >
                                  <v-icon v-if="stat.product.icon" 
                                    :style="stat.product.icon.startsWith('fa-') ? 'font-size: 10px' : 'font-size: 13px'" 
                                    color="white" 
                                    class="me-1">
                                    {{ stat.product.icon }}
                                  </v-icon>
                                  <span class="body-2 white--text font-weight-medium">{{ stat.name }}</span>
                                </v-chip>
                              </template>
                              <div class="d-flex flex-column black--text body-1">
                                <span class="font-weight-bold">{{ stat.name }}</span>
                                <span class="monospace-font body-2">{{ stat.product.name }}</span>
                                <span v-html="stat.tooltip" v-if="stat.name !== stat.tooltip"></span>
                              </div>
                            </v-tooltip>
                          </v-badge>
                          <div class="px-2 py-1 mr-1 align-self-start text-caption rounded orange--text" style="border:1px dashed orange">
                            <v-icon x-small color="orange"> 
                                {{dragging ? 'fa-grip' : 'fa-up-down-left-right'}}
                                </v-icon>
                          </div>
                        </drag>
                      </drop>
                    </div>
                  </div>
                </v-card>
                <div :class="activeTemplate.stats.length == maxStatsAllowed ? 'font-weight-bold red--text' : ''">
                  {{ activeTemplate.stats.length }} {{ $t('game.gamePossessionMap.of') }} {{ maxStatsAllowed }} {{ $t('general.maximum') }}
                </div>
              </v-col>
              <v-col v-else cols="12" class="pa-0" >
                <v-skeleton-loader type="list-item@5" class="flex-grow-1"></v-skeleton-loader>
              </v-col>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 d-flex justify-end">
          <v-btn
            :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'"
            rounded outlined class="elevation-0 monospace-font px-4"
            @click="onCancelClick()"
          >{{$t('general.cancel')}}</v-btn>
          <v-btn
            rounded class="elevation-0 monospace-font white--text px-4" color="primary"
            @click="onCreateClick()"
            :disabled="!hasStats || !activeTemplate.radarName"
          >{{ $t('general.create') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
//Components
import GeneralFilter from "../base/GeneralFilter";
//Services
import GeneralService from "@/services/general.service";
import StatService from "@/services/stat.service";
import UserService from "@/services/user.service";
//Utils
import { getProductInfoById } from "../../utils/functions";

//Custom Category Id
const CUSTOM_ID = 76;

export default {
  name: 'RadarTemplateFormModal',
  components: {
    GeneralFilter
  },
  filters: {
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'player'
    },
    positionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      show: false,
      activeTemplate: {visibilityId: 2, categoryId: CUSTOM_ID, defaultRadar: false, radarType: this.type, stats: []},
      positions: null,
      categories: null,
      categoryModel: null,
      disableCategories: false,
      availableProductIds: null,
      products: null,
      productModel: null,
      stats: null,
      filteredStats: [],
      requiredRules: [
          v => !!v || this.$t('general.formErrors.required'),
      ],
      radarTemplateRequiredRules: [
          v => !!v || this.$t('general.formErrors.required'),
          v => v && v.length <= 50 || this.$t('general.formErrors.maxLength') + " (50)"
      ],
      warningTextTemplates: "",
      //Maximum of stats allowed in a template
      maxStatsAllowed: 20,
      dragging:false
    };
  },
  created() {
  },
  computed: {
    hasStats(){
      return this.activeTemplate && this.activeTemplate.stats.length > 0
    },
    availableCategoriesByProduct(){
      //Filter by Product selected
      return this.categories.filter(c => c.product.id === this.productModel.id);
    },
    availableStats(){
      if(this.stats){
        //Filter Player or Team stats
        return this.stats.filter(s => s.radar && //Stats with is_radar = TRUE
                                      (this.activeTemplate.radarType === 'player' ? s.player : s.team));//Player or Team
      }
      return this.stats;
    },
    availableStatsByProduct(){
      if(this.availableStats && this.productModel){
        //availableStats are filtered by Player or Team
        return this.availableStats.filter(s => s.productId === this.productModel.id);
      }
      return this.availableStats;
    },
    availableStatsByCategory(){
      if(this.availableStatsByProduct){
        //Stats here are filtered by Player or Team and ProductId
        return this.availableStatsByProduct.filter(s => s.categoryId === this.categoryModel.id); //Category Selected
      }
      return this.availableStatsByProduct;
    },
  },
  methods: {
    async open() {
      this.show = true;
      this.disableCategories = false;
      this.loadPositions();
      await this.loadStats();
      this.loadProducts();
      this.loadCategories();
      
      //Set positionId as default position
      if(this.positionId){
        this.activeTemplate.positionId = this.positionId;
      }
    },
    close() {
      this.show = false;
    },
    async loadPositions(){
      let positionsRensponse =  await GeneralService.getPositions();
      this.positions = _.chain(positionsRensponse.data).forEach(p => p.name = this.$t(`positions.${p.nameEng.toLowerCase()}`)).value() ;
    },
    async loadStats(){
      let statsResponse =  await StatService.getAll();
      //Stats sorted by name
      this.stats = _.chain(statsResponse.data).map(s => {
        s.name = this.$t(`statsNaming.${s.code}`);
        s.tooltip = this.$t(`statsNaming.description.${s.code}`);
        s.product = getProductInfoById(s.productId, this.currentUserLanguage);
        return s;
      })
      .sortBy("name")
      .value();
      //Get productIds available for the user from available stats
      this.availableProductIds = [...new Set(this.stats.map(s => s.productId))];
     
    },
    loadProducts(){
      //Disable Physical and Arrigo for Team Radar
      this.products = [{id: 1, name: this.$t('roles.ROLE_EVENTING_DATA'), 
                        desc: this.$t('roles.ROLE_EVENTING_DATA_description'), 
                        icon: 'tune', 
                        active: this.availableProductIds.includes(1), 
                        disabled: !this.availableProductIds.includes(1)},
                       {id: 2, name: this.$t('roles.ROLE_PHYSICAL_DATA'), 
                        desc: this.$t('roles.ROLE_PHYSICAL_DATA_description'), 
                        icon: 'speed', 
                        active: !this.availableProductIds.includes(1) && this.availableProductIds.includes(2), 
                        disabled: this.activeTemplate.radarType === 'team' || !this.availableProductIds.includes(2)}, 
                       {id: 3, name: this.$t('roles.ROLE_ARRIGO'), 
                        desc: this.$t('roles.ROLE_ARRIGO_description'), 
                        icon: 'fa-wand-magic-sparkles', 
                        active: !this.availableProductIds.includes(1) && !this.availableProductIds.includes(2) && this.availableProductIds.includes(3), 
                        disabled: this.activeTemplate.radarType === 'team' || !this.availableProductIds.includes(3)}];             
               
      this.productModel = this.products.find(p => !p.disabled);
    },
    loadCategories(){
      //Get Categories from stats
      this.categories = _.uniqBy(this.availableStats.map(s => { 
        return {
          id: s.categoryId, 
          name: this.$t("faq.metrics.categories." + s.category),
          product: getProductInfoById(s.productId, this.currentUserLanguage)
        } }), 'id');
      
      //Sort categories by id asc and set active field
      this.categories = _.sortBy(this.categories, 'id').map((c, i) => {
        c.active = i == 0;
        return c;
      });
      //Set first category (Passing) as default
      this.categoryModel = this.availableCategoriesByProduct.find(c => c.active);
      this.filteredStats = this.availableStatsByCategory;
    },
    onProductClick(product){
      if(!product.active){
        this.products.forEach(p => {
          if(product.id === p.id || this.productModel.id === p.id)
            p.active = !p.active; 
        });
        this.productModel = product;
        this.onCategoryClick(this.availableCategoriesByProduct[0]);
        //On productClicked, clear activeTemplate stats
        this.activeTemplate.stats.length = 0;
        //On Product change set all stats inactive
        this.stats.forEach(s => {
            s.active = false; 
        });
      }
    },
    onCategoryClick(category) {
      if(!category.active){
          this.categories.forEach(c => {
          if(category.id === c.id || this.categoryModel.id === c.id)
            c.active = !c.active; 
        });
        this.categoryModel = category;
        this.filteredStats = this.availableStatsByCategory;
      }
    },
    onStatClick(statClicked){
      if (statClicked) {
        if(this.activeTemplate.stats.length < this.maxStatsAllowed || statClicked.active){
          statClicked.active = !statClicked.active;
          this.warningTextTemplates = "";
        }else{
          this.warningTextTemplates = this.$t('general.formErrors.maxLength') + ` (${this.maxStatsAllowed})`;
        }
        
        if(statClicked.active){
          //Stat clicked, add to the template 
          this.activeTemplate.stats.push(statClicked);
        }else{
          //Stat unclicked, remove from the template
          this.activeTemplate.stats = this.activeTemplate.stats.filter(s => statClicked.id !== s.id);
        }
      }
    },
    onFilterChange(filtered) {
      this.filteredStats = filtered;
    },
    onFilterInputChange(input) {
      if (input && input.length > 2) {
        this.disableCategories = true;
      }else{
        this.disableCategories = false;
        this.filteredStats = this.availableStatsByCategory;
      }
    },
    onCancelClick(){
      this.show = false;
      this.activeTemplate = {visibilityId: 2, categoryId: CUSTOM_ID, defaultRadar: false, radarType: this.type, stats: []};
    },
    async onCreateClick() {
      if(this.$refs.formRef.validate() && this.activeTemplate.stats.length > 0){

        this.activeTemplate.stats = this.activeTemplate.stats.map((s, i) => { return {
          id:s.id,
          name:s.name,
          order:i+1
        }});
     
        await UserService.postRadarTemplate(this.activeTemplate);

        this.$emit("save", this.activeTemplate.defaultRadar);

        this.postSearch(this.activeTemplate);
        this.$store.dispatch('changeToast', {
          showToast: true,
          text: 'collections.addSuccess',
          outlined: false,
          color: 'success darken-1',
          icon: "check_circle"
        });

        this.activeTemplate = {visibilityId: 2, categoryId: CUSTOM_ID, defaultRadar: false, radarType: this.type, stats: []};
       
        this.$forceUpdate();
      }
      
      this.show = false
    },
    async postSearch(template){
      const authorized = true;
      const category = 'custom';
      const tool = 'radar';
      let urlId = null;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let templateCopy = {...template};
      templateCopy.stats = _.chain(templateCopy.stats).forEach(s => {
        let statObject = _.find(this.stats,(stat) => stat.id === s.id);
        s.name = this.$t(`statsNaming.${statObject.code}`);
      }).orderBy("order","asc").value();

      if(templateCopy.positionId){
        templateCopy.positionCode = _.find(this.positions,(p) => p.id == templateCopy.positionId).nameEng;
        templateCopy.name = _.chain(templateCopy.positionCode).lowerCase().upperFirst().value() + " · Custom";
      }else{
        templateCopy.name = "Team Radar · Custom";
      }
      
      const metaInfo = JSON.stringify(templateCopy);
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser,metaInfo);
      UserService.putLastSearchRendered({category, tool, id:searchResponse.data})
    },
    onDropStat(data,index){
      _.pullAt(this.activeTemplate.stats,data.index);
      this.activeTemplate.stats.splice(index,0,data.stat);
      this.dragging=false;
      this.$forceUpdate();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/views/modals/RadarTemplateFormModal.vue